// ---------------------------------------------------------------------------
// D2: Reusable Skeleton Components
// ---------------------------------------------------------------------------
// Pulsing placeholder shown during data loading. Applied to all data pages.
// Usage: <Skeleton className="h-4 w-32" /> or <SkeletonCard /> or <SkeletonTable />

import { cn } from "../lib/utils";

interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className }: SkeletonProps) {
  return (
    <div
      className={cn(
        "animate-pulse rounded-md bg-[var(--color-muted,#e5e7eb)]",
        className,
      )}
    />
  );
}

function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  return (
    <div className={cn("space-y-2", className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={cn("h-4", i === lines - 1 ? "w-3/4" : "w-full")}
        />
      ))}
    </div>
  );
}

export function SkeletonCard({ className }: SkeletonProps) {
  return (
    <div className={cn("rounded-lg border p-4 space-y-3", className)}>
      <Skeleton className="h-5 w-2/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-4/5" />
      <div className="flex gap-2 pt-1">
        <Skeleton className="h-6 w-16 rounded-full" />
        <Skeleton className="h-6 w-20 rounded-full" />
      </div>
    </div>
  );
}

export function SkeletonTable({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex gap-4 pb-2 border-b">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className="h-4 flex-1" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIdx) => (
        <div key={rowIdx} className="flex gap-4 py-2">
          {Array.from({ length: cols }).map((_, colIdx) => (
            <Skeleton key={colIdx} className="h-4 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

export function SkeletonAvatar({ size = "md" }: { size?: "sm" | "md" | "lg" }) {
  const sizeClass = { sm: "h-8 w-8", md: "h-10 w-10", lg: "h-12 w-12" }[size];
  return <Skeleton className={cn("rounded-full", sizeClass)} />;
}

// Page-level skeleton layouts
function SkeletonPageList() {
  return (
    <div className="space-y-4 p-4">
      <Skeleton className="h-8 w-48" /> {/* Page title */}
      <div className="flex gap-2">
        <Skeleton className="h-10 w-64" /> {/* Search */}
        <Skeleton className="h-10 w-24" /> {/* Filter */}
      </div>
      {Array.from({ length: 8 }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

function SkeletonPageDetail() {
  return (
    <div className="space-y-6 p-4">
      <div className="flex items-center gap-4">
        <SkeletonAvatar size="lg" />
        <div className="space-y-2 flex-1">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <SkeletonText lines={5} />
      <SkeletonTable rows={4} cols={3} />
    </div>
  );
}

/** C05: Metric card skeleton — number + label + sparkline shape */
export function SkeletonMetricCard() {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4 space-y-2">
      <Skeleton className="h-3 w-20" />
      <Skeleton className="h-8 w-24" />
      <Skeleton className="h-4 w-full rounded" />
    </div>
  );
}

/** C05: Kanban card skeleton — avatar + title + stage pill */
export function SkeletonKanbanCard() {
  return (
    <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Skeleton className="h-7 w-7 rounded-full" />
        <Skeleton className="h-4 w-24" />
      </div>
      <Skeleton className="h-3 w-full" />
      <div className="flex gap-2">
        <Skeleton className="h-5 w-14 rounded-full" />
        <Skeleton className="h-5 w-10 rounded-full" />
      </div>
    </div>
  );
}

/** C05: Table row skeleton — matching typical column widths */
export function SkeletonTableRow({ cols = 5 }: { cols?: number }) {
  return (
    <div className="flex items-center gap-4 border-b border-[var(--color-border)] py-3 px-4">
      <Skeleton className="h-8 w-8 rounded-full" />
      {Array.from({ length: cols - 1 }).map((_, i) => (
        <Skeleton key={i} className={cn("h-4", i === 0 ? "w-32" : i === 1 ? "w-20" : "w-16")} />
      ))}
    </div>
  );
}

function SkeletonInbox() {
  return (
    <div className="flex h-full">
      {/* Conversation list */}
      <div className="w-80 border-r space-y-2 p-3">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="flex gap-3 p-2">
            <SkeletonAvatar size="sm" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-full" />
            </div>
          </div>
        ))}
      </div>
      {/* Message thread */}
      <div className="flex-1 p-4 space-y-4">
        <Skeleton className="h-6 w-48" />
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={cn("flex", i % 2 === 0 ? "justify-start" : "justify-end")}>
            <Skeleton className={cn("h-16 rounded-lg", i % 2 === 0 ? "w-2/3" : "w-1/2")} />
          </div>
        ))}
      </div>
    </div>
  );
}
